
import { reactive, toRefs, PropType, computed } from 'vue'
import { CtInventoryLocation } from '@/models/interfaces/content-type'
import { UUID } from '@Protium/vue-ozone-utils'
import { useStore } from 'vuex'
import InputHeader from '@/components/InputHeader.vue'
import QuantityContainer from '@/pages/Dashboard/components/QuantityContainer.vue'
import { Notify, useQuasar } from 'quasar'
import { ContentType } from '@/services/directr'
import { debounce } from 'lodash'
import Barcode from '@/components/Barcode.vue'


export default {
  name: 'LocationCard',
  components: { InputHeader, QuantityContainer, Barcode },
  props: {
    modelValue: {
      type: [String, Object] as PropType<UUID | CtInventoryLocation>
    },
    show: {
      type: Boolean,
      default: true
    },
    float: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    movable: {
      type: Boolean,
      default: false
    },
    count: {
      type: [String, Number],
      default: 0
    },
    closeLabel: {
      type: String,
      default: 'Close'
    },
    minWidth: {
      type: String,
      default: '320px'
    }
  },
  emits: ['update:modelValue', 'update:count', 'move', 'edit', 'save', 'close', 'search'],
  setup (props, { emit }) {
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']
    const $q = useQuasar()

    const state = reactive<{ moving: boolean, editing: boolean, changed: boolean, error?: { message: string, code: string | number}, resolved?: CtInventoryLocation}>({
      moving: false,
      editing: false,
      changed: false,
      error: undefined,
      resolved: undefined
    })

    function resolveLocation () {
      if (typeof props.modelValue === 'object') return props.modelValue
      else {
        $directr.get('inventory_location', props.modelValue)
          .then(({ items: [location] }) => {
            if (!location) state.error = { message: 'Location not found', code: 404 }
            else location.value = location
          })
        return { sys: { id: props.modelValue, loading: true }}
      }
    }

    const location = computed({
      get: () => {
        return resolveLocation()
      },
      set: (value) => {
        emit('update:modelValue', value)
      }
    })

    const decoupledCount = computed({
      get: () => props.count,
      set: value => emit('update:count', value)
    })

    function searchStock () {
      emit('search', location.value)
      Notify.create({message: 'Function not implemented at this time, sorry!'})
    }

    return {
      ...toRefs(state),
      location,
      decoupledCount,
      searchStock,
      commitMove: () => emit('move', location.value),
      emitClose: () => emit('close', location.value),
      emitSave: debounce(async () => {
        state.editing = false
        if (!location.value) return
        const id = location.value?.sys?.id
        const entry = {
          ...location.value,
          stock_item: location.value?.stock_item?.sys?.id,
          storage_location: location.value?.storage_location?.sys?.id,
          storage_type: location.value?.storage_type?.sys?.id,
          catalogues: location.value?.catalogues?.map(c => c?.sys?.id).filter(Boolean)
        }

        if (!id) throw Error('Could not update location, the location was unresolved or sys.id was undefined')
        const patched = await $directr.patch(ContentType.InventoryLocation, id, entry)
        state.changed = false
        emit('save', location.value)
        $q.notify({ message: 'Location updated', type: 'positive', classes: 'custom-notify success' })
      },500)
    }
  }
}
